import React, { useRef } from 'react';
import {
  Box,
  Button,
  colors,
  spacing,
  Flex,
  Grid,
  P,
  Popover,
  useI18nContext,
  Typography,
} from '@procore/core-react';
import { Check } from '@procore/core-icons';
import PricingSection from './components/Sections/Pricing';

import { appInstallPost } from '@/react/api';
import { MediaContainer, SidebarContainer } from '@/react/styles/v2';
import * as Styles from '@/react/styles';
import { AppHeader, InstallRequestButton } from '@/react/components';
import { useAppContext } from '@/react/context/AppContext';

import {
  OverviewSection,
  SideBar,
  SupportSection,
  FunctionalitySection,
  SecuritySection,
  SectionFooter,
} from './components/Sections';
import { sendAnalytics } from './utils';
import MediaCarousel from './components/MediaCarousel';
import { marketplaceColors } from '@/react/styles/constants';
import { ScrollAnchor, SectionNav } from './components/Sections/SectionNav';

const AppDetails = () => {
  const i18n = useI18nContext();
  const {
    appDetails: {
      app,
      metricsData,
      installationRequest,
      companyInstallationRequestsEnabled,
      companyName,
      user,
      wistiaId,
    },
  } = useAppContext();

  const {
    installed,
    developer_app_id,
    direct_installable,
    install_path,
    screenshots,
    seller,
    built_by,
    small_logo,
    small_logo_url,
  } = app;

  const colWidthsContent = { desktopSm: 8, tabletLg: 12 };
  const colWidthSidebar = { desktopSm: 4, tabletLg: 12 };

  const overviewRef = useRef(null);
  const functionalityRef = useRef(null);
  const pricingRef = useRef(null);
  const securityRef = useRef(null);
  const supportRef = useRef(null);

  const getActionButton = () => {
    if (installed) {
      return (
        <Box display="flex" gap="sm" padding="md 0">
          <Check />
          <Typography intent="h3" data-testid="app-installed">
            {i18n.t('labels.installed')}
          </Typography>
        </Box>
      );
    }

    if (direct_installable) {
      if (!user.isSignedIn) {
        return (
          <form method="post" action="/auth/procore">
            <input
              type="hidden"
              name="authenticity_token"
              value={document.querySelector('meta[name="csrf-token"]').content}
            />
            <Button
              type="submit"
              data-qa="install-signin-button"
              data-testid="install-signin-button"
              size="lg"
            >
              {i18n.t('appDetails.loginToInstall')}
            </Button>
          </form>
        );
      }

      if (!user.isCompanyAdmin) {
        if (companyInstallationRequestsEnabled) {
          return (
            <InstallRequestButton
              installRequestsPath={installationRequest?.apiPath}
              developerAppId={developer_app_id}
              requested={installationRequest?.present}
              sendAnalytics={sendAnalytics}
            />
          );
        }

        const content = (
          <Popover.Content>
            <Box padding="md">
              <P>{i18n.t('appDetails.mustBeAdmin')}</P>
            </Box>
          </Popover.Content>
        );

        return (
          <Popover overlay={content} trigger="hover" key="example-2">
            {() => (
              <Box>
                <Button
                  disabled
                  data-qa="install-signin-button"
                  data-testid="install-signin-button"
                  size="lg"
                >
                  {i18n.t('appDetails.installApp')}
                </Button>
              </Box>
            )}
          </Popover>
        );
      }

      if (install_path) {
        return (
          <Button
            id="app-install-button"
            data-testid="app-install-button"
            onClick={() => {
              sendAnalytics(
                'click',
                'Button',
                'Install App',
                'install_button.clicked'
              );
              appInstallPost(app);
              window.location = install_path;
            }}
            size="lg"
          >
            {i18n.t('appDetails.installApp')}
          </Button>
        );
      }
    }
    return null;
  };

  return (
    <Flex justifyContent="center">
      <Flex direction="column" flex="1">
        <AppHeader
          app={app}
          companyName={companyName}
          getActionButton={getActionButton}
          metricsData={metricsData}
          sendAnalytics={sendAnalytics}
          user={user}
        />

        <SectionNav
          sections={[
            {
              id: 'overview-intersection',
              title: 'appDetails.sectionNav.overview',
              ref: overviewRef,
            },
            {
              id: 'functionality-intersection',
              title: 'appDetails.sectionNav.functionality',
              ref: functionalityRef,
            },
            {
              id: 'pricing-intersection',
              title: 'appDetails.sectionNav.pricing',
              ref: pricingRef,
            },
            {
              id: 'security-intersection',
              title: 'appDetails.sectionNav.security',
              ref: securityRef,
            },
            {
              id: 'support-intersection',
              title: 'appDetails.sectionNav.support',
              ref: supportRef,
            },
          ]}
        />

        <Styles.Container
          style={{
            maxWidth: 'calc(100vw)',
            backgroundColor: marketplaceColors.stone40,
          }}
        >
          <Flex justifyContent="center">
            <MediaContainer>
              <Grid
                colStackCap="tabletMd"
                gutterX={{ tabletLg: 'xxl' }}
                gutterY={{ tabletSm: 'xl' }}
              >
                <Grid.Row>
                  <Grid.Col colWidth={colWidthsContent}>
                    <Flex justifyContent="center">
                      <MediaCarousel
                        appName={app?.name}
                        tagline={app?.search_description}
                        logoURL={small_logo_url || small_logo?.url}
                        screenshots={screenshots}
                        wistiaId={wistiaId}
                      />
                    </Flex>
                  </Grid.Col>
                  <Grid.Col colWidth={colWidthSidebar}>
                    <SidebarContainer>
                      <SideBar />
                    </SidebarContainer>
                  </Grid.Col>
                </Grid.Row>
              </Grid>
            </MediaContainer>
          </Flex>

          <Box style={{ width: '100%', backgroundColor: colors.white }}>
            <Styles.ContentContainer>
              <Flex direction="column">
                <ScrollAnchor ref={overviewRef} id="overview-intersection">
                  <OverviewSection description={app.description} />
                </ScrollAnchor>
                <hr
                  style={{
                    border: 0,
                    borderTop: `1px solid #BFB6AE`,
                    width: '100%',
                  }}
                />
                <ScrollAnchor
                  ref={functionalityRef}
                  id="functionality-intersection"
                >
                  <FunctionalitySection
                    howItWorks={app.how_it_works}
                    features={app.feature_bullets}
                    requirements={app.requirements}
                    tools={app.product_tools}
                  />
                </ScrollAnchor>
              </Flex>
            </Styles.ContentContainer>
          </Box>

          <Box
            style={{
              width: '100%',
              paddingTop: `${spacing.xxl}px`,
              paddingBottom: `${spacing.xxl}px`,
            }}
          >
            <Flex
              justifyContent="center"
              alignItems="flex-start"
              style={{ maxWidth: '780px', margin: 'auto', background: 'white' }}
            >
              <ScrollAnchor ref={pricingRef} id="pricing-intersection">
                <PricingSection
                  pricingDescription={app.pricing_description}
                  subscriptionRequired={app.subscription_required}
                  connectorRequired={app.connector_required}
                  hasTrials={app.has_trials}
                  pricingUrl={app.pricing_url}
                  email={app.contact_us_email}
                  soldBy={seller || built_by}
                  pricing={app.pricing}
                />
              </ScrollAnchor>
            </Flex>
          </Box>
          <Box
            id="security-section"
            data-testid="security-section"
            style={{ width: '100%', backgroundColor: colors.white }}
          >
            <Styles.ContentContainer>
              <ScrollAnchor ref={securityRef} id="security-intersection">
                <SecuritySection
                  app={app}
                  questionnaire={app.security_questionnaire}
                />
              </ScrollAnchor>
            </Styles.ContentContainer>
          </Box>

          <ScrollAnchor ref={supportRef} id="support-intersection">
            <SupportSection
              helpfulLinks={app.helpful_links}
              contactUsEmail={app.contact_us_email}
              website={app.website}
              name={app.name}
            />
          </ScrollAnchor>

          <SectionFooter />
        </Styles.Container>
      </Flex>
    </Flex>
  );
};

export default AppDetails;
